import { createApi } from "@reduxjs/toolkit/query/react";
import baseQueryWithReauth from "../../utils/basequery";
import { campaignApi } from "../CampaignService/campaignApi";

export const csvDataApi = createApi({
  reducerPath: "csvApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["filters", "users", "campaigns"],
  endpoints: (builder) => ({
    createCsvData: builder.mutation({
      query: (body) => ({
        url: "csv-data",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "csv" }, { type: "users" }],
    }),
    createCsvDataInCampaign: builder.mutation({
      query: (body) => ({
        url: "csv-data/campaign",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "csv" }, { type: "users" }],
    }),
    createApolloCsvData: builder.mutation({
      query: (body) => ({
        url: "apolo-messages",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "csv" }, { type: "users" }],
    }),
    getCsvDuplicateData: builder.query({
      query: (id) => ({
        url: `csv-data/${id}`,
        method: "GET",
      }),
      invalidatesTags: [{ type: "csv" }],
    }),
    getAllUsersData: builder.query({
      query: (args) => {
        const {
          pagination,
          sorting,
          filters,
          columns,
          searchQuery,
          filterType,
          campaignId,
          suppressedUserIds,
          versionType,
        } = args;
        return {
          url: "csv-data/users",
          params: {
            page: pagination.pageIndex,
            rows: pagination.pageSize,
            sorting: JSON.stringify(sorting),
            filters: filters,
            search: searchQuery,
            filterType: filterType,
            campaignId: campaignId,
            // suppressedUserIds: array of numbers [1, 2, 3] - stringified to "[1,2,3]" for query param
            // Backend will parse this JSON string back to array of numbers
            suppressedUserIds:
              suppressedUserIds && suppressedUserIds.length > 0
                ? JSON.stringify(suppressedUserIds)
                : undefined,
            versionType: versionType, // "filter" when filters applied, "suppress" when suppression uploaded, undefined otherwise
            // columns
          },
        };
      },
      providesTags: ["users"],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        if (args?.versionType && args?.campaignId) {
          try {
            await queryFulfilled;
            dispatch(
              campaignApi.util.invalidateTags([
                { type: "campaigns", id: args.campaignId },
                "campaigns",
              ])
            );
          } catch (error) {
            console.error(
              "Error invalidating campaign cache after version creation:",
              error
            );
          }
        }
      },
    }),
    exportUsers: builder.query({
      query: ({
        filters,
        sorting,
        batchCount,
        userCount,
        total,
        exportType = "raw",
        filterType,
        campaignId,
        suppressedUserIds,
      }) => {
        const params = {
          filters: JSON.stringify(filters),
          sorting: JSON.stringify(sorting),
          batchCount,
          userCount,
          total,
          exportType,
          filterType: filterType,
          campaignId: campaignId,
          suppressedUserIds:
            suppressedUserIds && suppressedUserIds.length > 0
              ? JSON.stringify(suppressedUserIds)
              : undefined,
        };

        return {
          url: "csv-data/export",
          params,
          responseHandler: async (response) => {
            const contentType = response.headers.get("content-type");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Generate filename based on export type
            const filename =
              exportType === "sendgrid"
                ? "users_sendgrid.csv"
                : "users_raw.csv";

            if (contentType.includes("zip")) {
              link.setAttribute("download", "users.zip");
            } else {
              link.setAttribute("download", filename);
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return undefined;
          },
        };
      },
    }),
    exportCampaignUsers: builder.query({
      query: (args) => {
        const { id, filters, exportType = "raw" } = args;
        return {
          url: "csv-data/export/" + id,
          params: {
            filters: JSON.stringify(filters),
            exportType,
          },
          responseHandler: async (response) => {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Generate filename based on export type
            const filename =
              exportType === "raw"
                ? "campaign_users_raw.csv"
                : exportType === "sendgrid"
                ? "campaign_users_sendgrid.csv"
                : exportType === "opens_clicks"
                ? "campaign_opens_clicks.csv"
                : "campaign_users.csv";

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Return something serializable or undefined since we handle download externally
            return undefined;
          },
        };
      },
    }),
    exportGroupedCampaignUsers: builder.query({
      query: (args) => {
        const { campaignIds, filters, exportType = "raw" } = args;
        console.log("API CALLED TO EXPORT GROUPED CAMPAIGN USERS", args);
        const ids = Array.isArray(campaignIds)
          ? campaignIds.join(",")
          : campaignIds;
        return {
          url: "csv-data/export/grouped",
          params: {
            ids,
            filters: filters ? JSON.stringify(filters) : undefined,
            exportType,
          },
          responseHandler: async (response) => {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const filename =
              exportType === "raw"
                ? `grouped_campaigns_${ids}_raw_export.csv`
                : exportType === "sendgrid"
                ? `grouped_campaigns_${ids}_sendgrid_export.csv`
                : `grouped_campaigns_${ids}_opens_clicks_export.csv`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return undefined;
          },
        };
      },
    }),
    getCompaniesAndDepartments: builder.query({
      query: () => ({
        url: "csv-data",
      }),
    }),

    DeleteDuplicateData: builder.mutation({
      query: (ids) => ({
        url: "csv-data",
        method: "DELETE",
        body: ids,
      }),
      invalidatesTags: [{ type: "users" }],
    }),
    updateDuplicateData: builder.mutation({
      query: (body) => ({
        url: `csv-data/duplicate`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["users"],
    }),
    getCampaignUsers: builder.query({
      query: (args) => {
        const { id, page, rows, filters } = args;
        return {
          url: `csv-data/users/${id}`,
          params: { page, rows, filters },
        };
      },
    }),
    getGroupedCampaignUsers: builder.query({
      query: (args) => {
        const { campaignIds, page, rows, filters } = args;
        const ids = Array.isArray(campaignIds)
          ? campaignIds.join(",")
          : campaignIds;
        console.log("campaignIds", campaignIds);
        return {
          url: `csv-data/users/grouped`,
          params: {
            ids: ids,
            page,
            rows,
            filters: filters ? JSON.stringify(filters) : undefined,
          },
        };
      },
    }),
    saveFilter: builder.mutation({
      query: (body) => ({
        url: "filters",
        method: "POST",
        body,
      }),
      invalidatesTags: ["filters"],
    }),
    getFilters: builder.query({
      query: (type) => ({
        url: "filters",
        method: "GET",
        params: type,
      }),
      providesTags: ["filters"],
    }),
    getFiltersWithConditions: builder.query({
      query: (type) => ({
        url: "filters/conditions",
        method: "GET",
        params: { type },
      }),
      providesTags: ["filters"],
    }),
    deleteFilter: builder.mutation({
      query: (id) => ({
        url: "filters/" + id,
        method: "DELETE",
        body: id,
      }),
      invalidatesTags: ["filters"],
    }),
    updateFilter: builder.mutation({
      query: ({ id, body }) => ({
        url: `filters/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["filters"],
    }),
  }),
});

export const {
  useCreateCsvDataMutation,
  useCreateCsvDataInCampaignMutation,
  useCreateApolloCsvDataMutation,
  useGetCsvDuplicateDataQuery,
  useGetAllUsersDataQuery,
  useLazyExportUsersQuery,
  useLazyExportCampaignUsersQuery,
  useLazyExportGroupedCampaignUsersQuery,
  useGetCompaniesAndDepartmentsQuery,
  useDeleteDuplicateDataMutation,
  useUpdateDuplicateDataMutation,
  useGetCampaignUsersQuery,
  useGetGroupedCampaignUsersQuery,
  useSaveFilterMutation,
  useGetFiltersQuery,
  useGetFiltersWithConditionsQuery,
  useDeleteFilterMutation,
  useUpdateFilterMutation,
} = csvDataApi;
